import {
  bomToolStats,
  getOptimalBomItemResults,
  buildBomExcelData,
} from "utils/bomToolUtils";

export const fmt = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

export const fmtPct = (n, tot) =>
  tot > 0 ? `${((n / tot) * 100).toFixed(1)}%` : "0%";

export const buildBomAnalysisReport = (res) => {
  const bItems = getOptimalBomItemResults(res.data?.bom_items);
  const eData = buildBomExcelData(res.data?.bom_items, bItems);
  const bStats = bomToolStats(res);

  const tRows = [];
  for (const row of eData.rows) {
    const dists = Object.keys(row.distPrices).filter(
      (d) => row.distPrices[d]?.unitPrice > 0,
    );
    for (let i = 0; i < dists.length; i++) {
      for (let j = i + 1; j < dists.length; j++) {
        const d1 = dists[i], d2 = dists[j];
        const p1 = row.distPrices[d1], p2 = row.distPrices[d2];
        if (!p1 || !p2) continue;
        const isWin = p1.unitPrice <= p2.unitPrice;
        const w = isWin ? d1 : d2, l = isWin ? d2 : d1;
        const wP = row.distPrices[w].unitPrice, lP = row.distPrices[l].unitPrice;
        const save = (lP - wP) * row.qty;
        if (save <= 0) continue;
        tRows.push({
          part: row.part_number,
          qty: row.qty,
          winner: w,
          wPrice: wP,
          loser: l,
          lPrice: lP,
          save,
          savePct: lP > 0 ? ((lP - wP) / lP) * 100 : 0,
        });
      }
    }
  }
  tRows.sort((a, b) => b.save - a.save);

  const winCounts = {};
  for (const r of tRows) winCounts[r.winner] = (winCounts[r.winner] || 0) + 1;

  let worstTotal = 0;
  for (const row of eData.rows) {
    const prices = Object.values(row.distPrices)
      .filter(Boolean)
      .map((p) => p.totalPrice);
    if (prices.length) worstTotal += Math.max(...prices);
  }

  const duels = {};
  for (let i = 0; i < eData.distributors.length; i++) {
    for (let j = i + 1; j < eData.distributors.length; j++) {
      const d1 = eData.distributors[i], d2 = eData.distributors[j];
      const shared = eData.rows.filter(
        (r) => r.distPrices[d1]?.unitPrice > 0 && r.distPrices[d2]?.unitPrice > 0,
      );
      let t1 = 0, t2 = 0, w1 = 0, w2 = 0;
      for (const row of shared) {
        const p1 = row.distPrices[d1].totalPrice, p2 = row.distPrices[d2].totalPrice;
        t1 += p1; t2 += p2;
        if (p1 <= p2) w1++; else w2++;
      }
      const mix = shared.reduce(
        (a, r) => a + Math.min(r.distPrices[d1].totalPrice, r.distPrices[d2].totalPrice),
        0,
      );
      duels[`${d1}|${d2}`] = { d1, d2, count: shared.length, t1, t2, w1, w2, mix };
      duels[`${d2}|${d1}`] = { d1: d2, d2: d1, count: shared.length, t1: t2, t2: t1, w1: w2, w2: w1, mix };
    }
  }

  const distSavings = {};
  for (const d of eData.distributors) {
    distSavings[d] = eData.rows.reduce((acc, row) => {
      if (!row.distPrices[d]?.totalPrice) return acc;
      const distCost = row.distPrices[d].totalPrice;
      const minCost = Math.min(
        ...Object.values(row.distPrices).filter(Boolean).map((p) => p.totalPrice),
      );
      return acc + (distCost - minCost);
    }, 0);
  }

  return {
    rows: tRows,
    totalSavings: tRows.reduce((a, r) => a + r.save, 0),
    winCounts,
    worstTotal,
    partsCompared: eData.rows.filter(
      (r) => Object.values(r.distPrices).filter(Boolean).length >= 2,
    ).length,
    optimalTotal: eData.optimalGrandTotal,
    optimalCoverageCount: eData.rows.filter((r) => r.optTotalPrice > 0).length,
    distributorSummary: eData.distributorSummary,
    distributors: eData.distributors,
    distSavings,
    duels,
    bomName: res.data?.bom?.file_name || "BOM Report",
    totalParts: bStats?.totalBomCount || 0,
  };
};

export const deriveReportVars = (reportData) => {
  const {
    rows = [],
    totalSavings = 0,
    winCounts = {},
    worstTotal = 0,
    partsCompared = 0,
    optimalTotal = 0,
    optimalCoverageCount = 0,
    distributorSummary = [],
    distributors: distNames = [],
    distSavings = {},
    duels = {},
    bomName = "BOM Intelligence Report",
    totalParts = 0,
  } = reportData || {};

  const sortedDistsDesc = [...distributorSummary].sort(
    (a, b) => (distSavings[b.distributor] || 0) - (distSavings[a.distributor] || 0),
  );
  const mostExpDist = sortedDistsDesc[0];
  const secondExpDist = sortedDistsDesc[1];
  const savedVsWorst = worstTotal > 0 ? worstTotal - optimalTotal : 0;
  const savedVsMostExp = mostExpDist ? distSavings[mostExpDist.distributor] || 0 : 0;
  const savedVsSecondExp = secondExpDist ? distSavings[secondExpDist.distributor] || 0 : 0;
  const totalWins = Object.values(winCounts).reduce((a, b) => a + b, 0);
  const getDuel = (d1, d2) =>
    duels[`${d1}|${d2}`] || { count: 0, t1: 0, t2: 0, w1: 0, w2: 0, mix: 0 };

  return {
    rows,
    totalSavings,
    winCounts,
    worstTotal,
    partsCompared,
    optimalTotal,
    optimalCoverageCount,
    distNames,
    distSavings,
    bomName,
    totalParts,
    sortedDistsDesc,
    mostExpDist,
    secondExpDist,
    savedVsWorst,
    savedVsMostExp,
    savedVsSecondExp,
    totalWins,
    getDuel,
  };
};
